// ============================================================
// mailFlags — Outlook's follow-up flag menu, resolved in the person's own day.
//
// Shared by the web Mail page (frontend/src/utils/mailFlags.js re-exports it)
// and the iOS Mail screens, so "This week" means the same Friday on both.
//
// The day is computed on the CLIENT and sent as YYYY-MM-DD — the API never
// guesses a day in the host's zone (api/src/mailClient.js flagPatch). This week
// is due the Friday of this week (today on a Friday or the weekend); Next week
// starts the following Monday and is due that Friday.
// ============================================================

const pad2 = (n: number) => String(n).padStart(2, "0");
export const mailLocalDate = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const plusDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export type FlagChoice = "today" | "tomorrow" | "thisWeek" | "nextWeek" | "noDate" | "complete" | "clear";
export interface FlagPayload { status: "flagged" | "complete" | "notFlagged"; startDate?: string; dueDate?: string; completedDate?: string }

export const FLAG_CHOICES: { key: FlagChoice; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "thisWeek", label: "This week" },
  { key: "nextWeek", label: "Next week" },
  { key: "noDate", label: "No date" },
];

/** { status, startDate?, dueDate? } for a flag-menu choice, relative to `now`. */
export function flagForChoice(choice: string, now: Date = new Date()): FlagPayload {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dow = today.getDay(); // 0 Sun … 6 Sat
  switch (choice) {
    case "today":
      return { status: "flagged", startDate: mailLocalDate(today), dueDate: mailLocalDate(today) };
    case "tomorrow": {
      const t = plusDays(today, 1);
      return { status: "flagged", startDate: mailLocalDate(t), dueDate: mailLocalDate(t) };
    }
    case "thisWeek": {
      const toFriday = dow <= 5 ? 5 - dow : 0;
      const due = dow === 6 || dow === 0 ? today : plusDays(today, toFriday);
      return { status: "flagged", startDate: mailLocalDate(today), dueDate: mailLocalDate(due) };
    }
    case "nextWeek": {
      const toMonday = ((8 - dow) % 7) || 7;
      const mon = plusDays(today, toMonday);
      return { status: "flagged", startDate: mailLocalDate(mon), dueDate: mailLocalDate(plusDays(mon, 4)) };
    }
    case "noDate":
      return { status: "flagged" };
    case "complete":
      return { status: "complete", completedDate: mailLocalDate(today) };
    case "clear":
    default:
      return { status: "notFlagged" };
  }
}

/** How a flagged row says when it is due — "Due today", "Overdue", "Due Fri, Sep 26". */
export function flagDueLabel(dueDate: string | null | undefined, now: Date = new Date()): { text: string; overdue: boolean } | null {
  if (!dueDate || !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return null;
  const today = mailLocalDate(now);
  if (dueDate < today) return { text: "Overdue", overdue: true };
  if (dueDate === today) return { text: "Due today", overdue: false };
  if (dueDate === mailLocalDate(plusDays(now, 1))) return { text: "Due tomorrow", overdue: false };
  const [y, m, d] = dueDate.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return { text: `Due ${dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`, overdue: false };
}
